import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Heart, Sparkles, ArrowRight, RefreshCw } from "lucide-react";

// Rich benefits table — each tier includes everything the tier below has,
// plus the row-specific ones. Rendered as a compact bullet list on each card.
const TIER_BENEFITS = {
  mobile: [
    "Unlimited streaming on 1 device",
    "SD 480p — perfect for phones",
    "Every approved title",
    "Cancel anytime",
  ],
  basic: [
    "Unlimited streaming on 2 devices",
    "HD 720p — crisp on tablets",
    "Every approved title",
    "Cancel anytime",
  ],
  standard: [
    "Unlimited streaming on 3 devices",
    "Full HD 1080p — great on TVs",
    "Every approved title",
    "Cancel anytime",
  ],
  premium: [
    "Unlimited streaming on 4 devices",
    "4K UHD + HDR — cinema-grade",
    "Every approved title",
    "Cancel anytime",
  ],
};

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  const loadConfig = () => api.get("/subscription/config").then(({ data }) => setConfig(data));
  useEffect(() => { loadConfig(); }, []);

  // Auto-highlight the tier the user is on OR their pending switch OR the top plan.
  const currentTier = user?.subscription_tier || null;
  const pendingTier = user?.pending_subscription_tier || null;
  const isActive = user?.subscription_status === "active";

  useEffect(() => {
    if (!selectedTier && config?.tiers?.length) {
      setSelectedTier(pendingTier || currentTier || config.tiers[config.tiers.length - 1].slug);
    }
  }, [config, currentTier, pendingTier, selectedTier]);

  const subscribe = async (slug) => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/initialize", {
        callback_url: `${window.location.origin}/payment/callback`,
        tier: slug,
      });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setLoading(false);
    }
  };

  const devGrant = async (slug) => {
    setLoading(true);
    try {
      await api.post("/subscription/dev/grant", { tier: slug });
      await refresh();
      toast.success(`Activated ${slug} tier (dev mode).`);
    } catch (e) {
      toast.error("Failed");
    } finally {
      setLoading(false);
    }
  };

  const scheduleSwitch = async (slug) => {
    if (!window.confirm(`Switch to ${labelFor(slug)} on your next renewal? Your current plan runs until ${expiryStr}.`)) return;
    setLoading(true);
    try {
      await api.post("/subscription/pending-tier", { tier: slug });
      await refresh();
      await loadConfig();
      toast.success("Switch scheduled — takes effect on renewal.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't schedule switch.");
    } finally {
      setLoading(false);
    }
  };

  const clearSwitch = async () => {
    setLoading(true);
    try {
      await api.delete("/subscription/pending-tier");
      await refresh();
      await loadConfig();
      toast.success("Scheduled switch cancelled.");
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the current period ends.")) return;
    setLoading(true);
    try {
      await api.post("/subscription/cancel");
      await refresh();
      toast.success("Subscription cancelled");
    } finally {
      setLoading(false);
    }
  };

  const expiryStr = useMemo(() => {
    if (!user?.subscription_expires_at) return "—";
    return new Date(user.subscription_expires_at).toLocaleDateString();
  }, [user?.subscription_expires_at]);

  const labelFor = (slug) => {
    const t = (config?.tiers || []).find((x) => x.slug === slug);
    return t ? `${t.name} (₦${t.amount_ngn.toLocaleString()}/mo)` : slug;
  };

  const isConfigured = !!config?.configured;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="subscription-page">
      <div className="text-center mb-14">
        <p className="text-eyebrow flex items-center justify-center gap-2 mb-4">
          <Sparkles className="h-3.5 w-3.5 text-[#EAB308]" /> Christoreel Membership
        </p>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter">
          {isActive ? "Manage your plan" : "Pick a plan"}
        </h1>
        <p className="mt-4 text-lg text-white/60 max-w-xl mx-auto">
          {isActive
            ? "Four picture-quality tiers. Same catalogue. Switch on renewal — no proration, no surprises."
            : "Four picture-quality tiers. Same catalogue of approved African Christian films. Cancel anytime."}
        </p>
      </div>

      {/* Currently active — show status + pending-switch banner. */}
      {isActive && (
        <div className="max-w-3xl mx-auto mb-10 p-5 rounded-xl bg-green-500/10 border border-green-500/30" data-testid="active-sub-banner">
          <div className="flex items-start gap-3">
            <Heart className="h-5 w-5 text-green-400 fill-green-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-green-400">
                You're on {labelFor(currentTier || "premium")}
              </p>
              <p className="text-sm text-white/70 mt-1">Renews / ends {expiryStr}</p>
              {pendingTier && pendingTier !== currentTier && (
                <div className="mt-3 p-3 rounded-lg bg-white/5 border border-white/10 flex items-center gap-3 flex-wrap" data-testid="pending-switch-banner">
                  <ArrowRight className="h-4 w-4 text-[#EAB308] shrink-0" />
                  <p className="text-sm text-white/80 flex-1 min-w-0">
                    Switching to <b className="text-white">{labelFor(pendingTier)}</b> on {expiryStr}. Log in on/after that date to re-checkout.
                  </p>
                  <button onClick={clearSwitch} disabled={loading} className="text-xs text-white/50 hover:text-white transition inline-flex items-center gap-1" data-testid="clear-pending-switch-button">
                    <RefreshCw className="h-3 w-3" /> Undo
                  </button>
                </div>
              )}
            </div>
            <button onClick={cancel} disabled={loading} className="ghost-btn !py-1.5 !px-3 text-xs hover:border-red-500/50 hover:text-red-400 shrink-0" data-testid="cancel-sub-button">
              Cancel plan
            </button>
          </div>
        </div>
      )}

      {/* Tier grid — 4 cards. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto" data-testid="tier-grid">
        {(config?.tiers || []).map((t) => {
          const isCurrent = isActive && currentTier === t.slug;
          const isPending = pendingTier === t.slug && !isCurrent;
          const featured = t.slug === "premium";
          return (
            <div
              key={t.slug}
              className={`card-surface p-6 relative overflow-hidden transition ${
                isCurrent
                  ? "!border-green-500/40 ring-1 ring-green-500/30"
                  : isPending
                  ? "!border-[#EAB308]/40 ring-1 ring-[#EAB308]/20"
                  : featured
                  ? "!border-[#E65F2B]/30"
                  : ""
              }`}
              data-testid={`tier-card-${t.slug}`}
            >
              {featured && !isCurrent && (
                <div className="absolute top-0 right-0 text-[10px] font-bold tracking-widest text-[#E65F2B] bg-[#E65F2B]/10 border-l border-b border-[#E65F2B]/30 px-3 py-1 rounded-bl-lg">
                  BEST
                </div>
              )}
              {isCurrent && (
                <div className="absolute top-0 right-0 text-[10px] font-bold tracking-widest text-green-400 bg-green-500/10 border-l border-b border-green-500/30 px-3 py-1 rounded-bl-lg" data-testid={`tier-card-${t.slug}-current-badge`}>
                  CURRENT
                </div>
              )}
              {isPending && (
                <div className="absolute top-0 right-0 text-[10px] font-bold tracking-widest text-[#EAB308] bg-[#EAB308]/10 border-l border-b border-[#EAB308]/30 px-3 py-1 rounded-bl-lg" data-testid={`tier-card-${t.slug}-pending-badge`}>
                  ON RENEWAL
                </div>
              )}

              <p className="text-eyebrow text-[#EAB308]">{t.quality_label} · {t.resolution}</p>
              <h3 className="font-display text-2xl font-black mt-1">{t.name}</h3>
              <p className="text-xs text-white/40 mt-1 h-8">{t.tagline}</p>

              <div className="mt-4">
                <p className="font-display text-4xl font-black">
                  ₦{t.amount_ngn.toLocaleString()}
                  <span className="text-sm text-white/40 font-medium">/mo</span>
                </p>
                <p className="text-[10px] text-white/30 mt-1">
                  {t.recurring ? "Auto-billed by Paystack" : "One-off charge · manual re-checkout to renew"}
                </p>
              </div>

              <ul className="mt-4 space-y-2 min-h-[128px]">
                {(TIER_BENEFITS[t.slug] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-white/70">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#E65F2B] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>

              <div className="mt-5">
                {isCurrent ? (
                  <button disabled className="ghost-btn w-full justify-center opacity-40 cursor-default" data-testid={`tier-card-${t.slug}-current-button`}>
                    Current plan
                  </button>
                ) : isActive ? (
                  <button
                    onClick={() => scheduleSwitch(t.slug)}
                    disabled={loading}
                    className={featured ? "ember-btn w-full justify-center" : "ghost-btn w-full justify-center"}
                    data-testid={`tier-card-${t.slug}-switch-button`}
                  >
                    Switch on renewal
                  </button>
                ) : isConfigured ? (
                  <button
                    onClick={() => subscribe(t.slug)}
                    disabled={loading}
                    className={featured ? "ember-btn w-full justify-center" : "ghost-btn w-full justify-center"}
                    data-testid={`tier-card-${t.slug}-subscribe-button`}
                  >
                    {loading && selectedTier === t.slug ? "Redirecting..." : `Subscribe — ₦${t.amount_ngn.toLocaleString()}`}
                  </button>
                ) : (
                  <button
                    onClick={() => devGrant(t.slug)}
                    disabled={loading}
                    className="ember-btn w-full justify-center"
                    data-testid={`tier-card-${t.slug}-dev-button`}
                  >
                    Activate (Dev)
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isConfigured && (
        <div className="max-w-md mx-auto mt-8 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-200 text-center" data-testid="paystack-not-configured">
          Paystack keys not yet configured. Use the Activate (Dev) button on any card to preview streaming.
        </div>
      )}

      <p className="mt-14 text-center text-xs text-white/30 max-w-2xl mx-auto">
        Every subscription funds moderation, storage, and a transparent share of revenue for the African filmmakers whose work you stream. Cancel anytime — access continues until the end of your billing period.
      </p>
    </div>
  );
}
